import { useCallback, useState } from "react";
import { AxiosInstance } from "axios";
import {
  createPosting,
  createProcess,
  createPbsLiveInsertion,
  getActivePostingForClient,
  runAspWorkflow,
  saveScheduleSlotSelection,
  SlotSelectionResponse,
  updatePosting,
} from "@/services/staffing/Staffing";
import {
  CLIENT_AVAILABILITY_STATUS,
  CLIENT_AVAILABILITY_USER_TYPE,
  getClientAvailability,
  upsertClientAvailability,
} from "@/services/staffing/Availability";
import {
  ServiceRequestType,
  ClientOption,
  ClientScheduleSlot,
} from "@/types/staffing/serviceRequest";
import { AspWorkflowStep } from "@/types/staffing/asp";
import { User } from "@/types/common/user";
import { FieldError, getClientInitials, getPostingServiceType } from "../utils";
import {
  buildClientAvailabilityData,
  buildSlotSelectionPayload,
  buildSlotSelectionReferenceId,
  getClientScheduleError,
  isSlotRangeValid,
} from "../clientScheduleUtils";
import { useServiceRequestClients } from "./useServiceRequestClients";
import { useStaffSearch } from "./useStaffSearch";

class ServiceAlreadyRequestedError extends Error {
  constructor(public requestedBy: string) {
    super("Service already requested");
  }
}

interface UseServiceRequestFormArgs {
  apiClient: AxiosInstance;
  currentUser: User;
  userLoading: boolean;
  onBack?: () => void;
}

export function useServiceRequestForm({
  apiClient,
  currentUser,
  userLoading,
}: UseServiceRequestFormArgs) {
  const [serviceType, setServiceType] = useState<ServiceRequestType | "">("");
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(
    null,
  );
  const [clientService, setClientService] = useState("");
  const [language, setLanguage] = useState("English");
  const [clientSchedule, setClientSchedule] = useState<ClientScheduleSlot[]>([]);
  /** Toast message returned by the schedule save (also used when the API saved nothing). */
  const [scheduleMessage, setScheduleMessage] = useState<string | null>(null);
  const [needPriority, setNeedPriority] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FieldError>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [aspModalOpen, setAspModalOpen] = useState(false);
  const [aspWorkflowResults, setAspWorkflowResults] = useState<
    AspWorkflowStep[] | undefined
  >(undefined);

  const { clients, loadingClients } = useServiceRequestClients(
    currentUser.LoginName,
    userLoading,
  );
  const staffSearch = useStaffSearch(apiClient);

  const handleServiceTypeChange = useCallback(
    (val: string) => {
      setServiceType(val as ServiceRequestType);
      setClientService("");
      setClientSchedule([]);
      setErrors({});
      staffSearch.resetStaffSearch();
    },
    [staffSearch],
  );

  const handleClientChange = useCallback(
    (clientId: string) => {
      const client = clients.find((c) => c.id === Number(clientId)) ?? null;
      setSelectedClient(client);
      // The schedule belongs to one client; never carry it over to another.
      setClientSchedule([]);
      setErrors((prev) => ({ ...prev, clientId: undefined, clientSchedule: undefined }));
    },
    [clients],
  );

  const validate = (): boolean => {
    const errs: FieldError = {};
    if (!serviceType) errs.serviceType = "Service type is required";
    if (!selectedClient) errs.clientId = "Client name is required";
    if (serviceType === "Add Provider" && !staffSearch.selectedStaff)
      errs.staffUser = "Staff member is required";
    if (serviceType === "I need help finding staff") {
      if (!clientService) errs.clientService = "Client service is required";
      if (!language) errs.language = "Language is required";
    }
    if (serviceType) {
      // Add Provider goes to the scheduling slot endpoint, which takes one range per day;
      // "I need help finding staff" writes client availability, which allows several.
      const scheduleError = getClientScheduleError(clientSchedule, {
        singleRangePerDay: serviceType === "Add Provider",
      });
      if (scheduleError) {
        errs.clientSchedule = scheduleError;
      } else if (
        serviceType === "I need help finding staff" &&
        selectedClient &&
        !selectedClient.encryptedId
      ) {
        errs.clientSchedule =
          "This client's availability cannot be updated (no availability id on record). Please contact support.";
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const getOrCreatePosting = async (
    clientId: number,
    postingServiceType: string,
    errorIfRequestedByOther: boolean,
  ): Promise<number> => {
    const res = await getActivePostingForClient(
      clientId,
      postingServiceType,
      apiClient,
    );
    const existing = res.data?.value?.[0];
    if (existing) {
      const me = "positive\\" + currentUser.LoginName;
      const requestedBy: string | null = existing.ServiceRequestedBy;
      if (!requestedBy) {
        await updatePosting(
          existing.Id,
          { status: existing.Status, serviceRequestedBy: me },
          apiClient,
        );
      } else if (
        requestedBy.toLowerCase() !== me.toLowerCase() &&
        errorIfRequestedByOther
      ) {
        throw new ServiceAlreadyRequestedError(requestedBy);
      }
      return existing.Id;
    }
    await createPosting(
      {
        clientId,
        serviceType: postingServiceType,
        telehealth: false,
        serviceRequestedBy: "positive\\" + currentUser.LoginName,
      },
      apiClient,
    );
    const fresh = await getActivePostingForClient(
      clientId,
      postingServiceType,
      apiClient,
    );
    return fresh.data.value[0].Id;
  };

  /**
   * Add Provider: submits the entered schedule to the scheduling slot-selection
   * endpoint as Draft blocks on the selected staff member. `referenceId` is
   * {processId}-{staffLogin}-{clientId}. The endpoint answers 200 with a toast
   * `message` both when it saved and when it rejected the times as unavailable,
   * so the message is kept for the success screen.
   */
  const saveStaffScheduleSelection = async (referenceId: string) => {
    const res = await saveScheduleSlotSelection(
      buildSlotSelectionPayload(clientSchedule, referenceId, currentUser.LoginName),
      apiClient,
    );
    const body: SlotSelectionResponse | undefined = res.data;
    setScheduleMessage(body?.message ?? null);
  };

  /**
   * "I need help finding staff" (task 11062): replaces the client's saved
   * availability with the schedule entered on the form. The upsert overwrites
   * the whole row, so the existing description is read first and carried over
   * (best-effort: a missing row is fine).
   */
  const saveClientAvailability = async (encryptedClientId: string) => {
    let description: string | undefined;
    try {
      const existing = await getClientAvailability(encryptedClientId, apiClient);
      description = existing.data?.description ?? undefined;
    } catch (err: any) {
      if (err?.response?.status !== 404) {
        console.warn(
          "Could not read existing client availability; description will not be preserved.",
          err,
        );
      }
    }

    await upsertClientAvailability(
      {
        encryptedClientId,
        data: buildClientAvailabilityData(clientSchedule),
        status: CLIENT_AVAILABILITY_STATUS,
        userType: CLIENT_AVAILABILITY_USER_TYPE,
        description,
      },
      apiClient,
    );
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!validate()) return;
    setSaving(true);
    try {
      const clientId = selectedClient!.id;

      if (serviceType === "Add Provider" && staffSearch.selectedStaff) {
        const postingServiceType = getPostingServiceType(
          staffSearch.selectedStaff.Role,
        );
        const postingId = await getOrCreatePosting(
          clientId,
          postingServiceType,
          false,
        );
        const match = staffSearch.selectedStaff;
        const processRes = await createProcess(
          {
            postingId,
            staff: "positive\\" + staffSearch.selectedStaff.LoginName,
            source: "Service Request",
            applicant: false,
            author: currentUser.LoginName,
            adHocGroup: {
              clientId,
              clientName: selectedClient?.name ?? "",
              staffFullName: match?.FullName ?? "",
              staffRole: match?.Role ?? "",
              staffRegion: match?.Region ?? "",
            },
          },
          apiClient,
        );

        // Save the schedule as Draft blocks on the staff member BEFORE the ASP
        // workflow: its ScheduleCleanup step finalizes the winner's blocks for
        // this client once the placement succeeds.
        const processId: number | undefined = processRes.data?.id;
        if (processId) {
          await saveStaffScheduleSelection(
            buildSlotSelectionReferenceId(
              processId,
              staffSearch.selectedStaff.LoginName,
              clientId,
            ),
          );
        } else {
          setScheduleMessage(
            "Not submitted — the staffing process did not return an id.",
          );
        }

        const aspRes = await runAspWorkflow(
          {
            staffLoginName: staffSearch.selectedStaff.LoginName,
            clientId,
            source: "Service Request",
            processId: processRes.data?.id,
          },
          apiClient,
        );
        const aspSteps: AspWorkflowStep[] = aspRes.data ?? [];
        setAspWorkflowResults(aspSteps);
        setAspModalOpen(true);

        const aspSucceeded = aspSteps.every((s) => s.status === "Success");
        if (aspSucceeded) {
          await createPbsLiveInsertion(
            {
              pbsLiveInsertionsId: 338,
              identifierValue: staffSearch.selectedStaff.LoginName,
              receiversRegion: "",
              dynamicTags: {
                $CLIENTNAME: getClientInitials(selectedClient!.name),
              },
              retry: false,
            },
            apiClient,
          );
        }
      }

      if (serviceType === "I need help finding staff") {
        await getOrCreatePosting(clientId, clientService, true);
        await apiClient.put(
          `staffing/servicerequest/clients/${clientId}/language`,
          { language },
        );
        await saveClientAvailability(selectedClient!.encryptedId!);
      }

      setSubmitSuccess(true);
    } catch (err: any) {
      const data = err?.response?.data;
      let message: string;
      if (err instanceof ServiceAlreadyRequestedError) {
        const requester = err.requestedBy.replace(/^positive\\/i, "");
        message = `A staffing request for this client and service already exists (requested by ${requester}).`;
      } else if (data?.code === "StaffingProcess.Conflict.StaffAlreadyOnPosting") {
        const staff = staffSearch.selectedStaff;
        message = staff
          ? `${staff.FullName} is already assigned to this client.`
          : "This staff member is already assigned to this client.";
      } else {
        message =
          data?.detail ??
          data?.error ?? // PBS Live toast contract (scheduling slot selection)
          data?.message ??
          err?.message ??
          "Failed to submit. Please try again.";
      }
      setSubmitError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setServiceType("");
    setSelectedClient(null);
    setClientService("");
    setLanguage("English");
    setClientSchedule([]);
    setScheduleMessage(null);
    setNeedPriority(false);
    setErrors({});
    setSubmitError(null);
    setSubmitSuccess(false);
    setAspWorkflowResults(undefined);
    staffSearch.resetStaffSearch();
  };

  const isAddProvider = serviceType === "Add Provider";
  const isNeedHelp = serviceType === "I need help finding staff";
  const hasServiceType = serviceType !== "";

  const hasClientSchedule = clientSchedule.some(isSlotRangeValid);
  /** Why Submit is disabled, or undefined when it may be clicked. */
  const submitBlockedReason: string | undefined = !hasServiceType
    ? undefined
    : !selectedClient
      ? "Select a client to continue."
      : isAddProvider && !staffSearch.selectedStaff
        ? "Select a staff member to continue."
        : !hasClientSchedule
          ? "Add at least one day and time range to the client schedule to submit."
          : undefined;

  return {
    serviceType,
    handleServiceTypeChange,
    clients,
    loadingClients,
    selectedClient,
    handleClientChange,
    ...staffSearch,
    clientService,
    setClientService,
    language,
    setLanguage,
    clientSchedule,
    setClientSchedule,
    scheduleMessage,
    needPriority,
    setNeedPriority,
    saving,
    errors,
    submitError,
    submitSuccess,
    handleSubmit,
    handleReset,
    aspModalOpen,
    setAspModalOpen,
    aspWorkflowResults,
    isAddProvider,
    isNeedHelp,
    hasServiceType,
    submitBlockedReason,
  };
}
